import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { Box } from '@mui/material';
import LogoLoader from '../components/ui/LogoLoader';
import { pmaQueryClient } from './lib/queryClient';
import { PmaProvider, usePermission } from './PmaContext';
import PmaShell from './components/PmaShell';
import { GlobalSavingOverlay } from './components/SavingOverlay';
import { FileViewerHost } from './components/editor/fileViewer';

const HomePage = lazy(() => import('./pages/HomePage'));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage'));
const TimesheetPage = lazy(() => import('./pages/TimesheetPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const CreateTaskPage = lazy(() => import('./pages/task/CreateTaskPage'));
const TaskDetailPage = lazy(() => import('./pages/task/TaskDetailPage'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminWorkspacePage = lazy(() => import('./pages/admin/WorkspaceAndCategories').then((m) => ({ default: m.AdminWorkspacePage })));
const AdminCategoriesPage = lazy(() => import('./pages/admin/WorkspaceAndCategories').then((m) => ({ default: m.AdminCategoriesPage })));
const AdminUsersPage = lazy(() => import('./pages/admin/UsersAndTeams').then((m) => ({ default: m.AdminUsersPage })));
const AdminTeamsPage = lazy(() => import('./pages/admin/UsersAndTeams').then((m) => ({ default: m.AdminTeamsPage })));
const AdminRolesPage = lazy(() => import('./pages/admin/RolesSecurityAudit').then((m) => ({ default: m.AdminRolesPage })));
const AdminRoleDetailPage = lazy(() => import('./pages/admin/RolesSecurityAudit').then((m) => ({ default: m.AdminRoleDetailPage })));
const AdminSecurityPage = lazy(() => import('./pages/admin/RolesSecurityAudit').then((m) => ({ default: m.AdminSecurityPage })));
const AdminAuditLogPage = lazy(() => import('./pages/admin/RolesSecurityAudit').then((m) => ({ default: m.AdminAuditLogPage })));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const CreateProjectPage = lazy(() => import('./pages/CreateProjectPage'));
const ProjectWorkspace = lazy(() => import('./pages/project/ProjectWorkspace'));
const OverviewTab = lazy(() => import('./pages/project/OverviewTab'));
const TeamTab = lazy(() => import('./pages/project/TeamTab'));
const SettingsTab = lazy(() => import('./pages/project/SettingsTab'));
const ProposalsTab = lazy(() => import('./pages/project/ProposalsTab').then((m) => ({ default: m.ProposalsTab })));
const ProposalDetailPage = lazy(() => import('./pages/project/ProposalsTab').then((m) => ({ default: m.ProposalDetailPage })));
const TasksTab = lazy(() => import('./pages/project/TasksTab').then((m) => ({ default: m.TasksTab })));
const TimelineTab = lazy(() => import('./pages/project/TasksTab').then((m) => ({ default: m.TimelineTab })));

const Loading = () => (
  <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}>
    <LogoLoader />
  </Box>
);

/** Gates the admin area on the access_admin permission (the API enforces it too). */
function AdminGate() {
  const { can } = usePermission();
  return can('access_admin') ? <AdminLayout /> : <Navigate to="/pma" replace />;
}

/** Root of the project-management module, mounted at /pma/*. */
export default function PmaApp() {
  return (
    <QueryClientProvider client={pmaQueryClient}>
      <PmaProvider>
        <GlobalSavingOverlay />
        <FileViewerHost />
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route element={<PmaShell />}>
              <Route index element={<HomePage />} />
              <Route path="tasks" element={<MyTasksPage />} />
              <Route path="tasks/new" element={<CreateTaskPage />} />
              <Route path="tasks/:taskId" element={<TaskDetailPage />} />
              <Route path="timesheet" element={<TimesheetPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="projects" element={<ProjectsPage />} />
              <Route path="projects/new" element={<CreateProjectPage />} />
              <Route path="projects/:projectId/tasks/new" element={<CreateTaskPage />} />
              <Route path="projects/:projectId" element={<ProjectWorkspace />}>
                <Route index element={<OverviewTab />} />
                <Route path="proposals" element={<ProposalsTab />} />
                <Route path="proposals/:proposalId" element={<ProposalDetailPage />} />
                <Route path="tasks" element={<TasksTab />} />
                <Route path="timeline" element={<TimelineTab />} />
                <Route path="team" element={<TeamTab />} />
                <Route path="settings" element={<SettingsTab />} />
              </Route>
              <Route path="admin" element={<AdminGate />}>
                <Route index element={<Navigate to="workspace" replace />} />
                <Route path="workspace" element={<AdminWorkspacePage />} />
                <Route path="task-categories" element={<AdminCategoriesPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="teams" element={<AdminTeamsPage />} />
                <Route path="roles" element={<AdminRolesPage />} />
                <Route path="roles/:roleId" element={<AdminRoleDetailPage />} />
                <Route path="security" element={<AdminSecurityPage />} />
                <Route path="audit-log" element={<AdminAuditLogPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/pma" replace />} />
            </Route>
          </Routes>
        </Suspense>
      </PmaProvider>
    </QueryClientProvider>
  );
}
